/**
 * API Widget (côté visiteur) : pas d'auth, header X-Site-Token requis.
 * Utilisé par le ChatWidget sur le site client.
 * En mode embed (script externe), setWidgetApiBaseUrl() est appelé avant le rendu.
 */

let BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000/api';

/** Définit l’URL de base de l’API (pour le script d’intégration externe). */
export function setWidgetApiBaseUrl(url: string): void {
  BASE_URL = url.replace(/\/$/, '');
}

export type WidgetConfig = {
  site_id?: number;
  welcome_message?: string | null;
  widget_color?: string | null;
  widget_position?: string | null;
  has_online_agent: boolean;
};

type ApiVisitor = { id: number; first_name?: string | null; email?: string | null };
export type MessageSender = { id: number; name: string; avatar?: string | null };
export type ReplyPreview = {
  id: number;
  content: string;
  sender_type: string;
  sender_name?: string | null;
};
type ApiMessage = {
  id: number;
  content: string;
  sender_type: string;
  created_at: string;
  sender?: MessageSender;
  reply_to?: ReplyPreview | null;
  edited_at?: string | null;
  original_content?: string | null;
};
type ApiConversation = { id: number; visitor_id: number; status: string; [k: string]: unknown };

async function widgetFetch<T>(
  path: string,
  siteToken: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers: HeadersInit = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    'Accept': 'application/json',
    'X-Site-Token': siteToken,
    ...(options.headers as Record<string, string>),
  };
  const res = await fetch(url, { ...options, headers });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      message = data.message ?? message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

export async function getWidgetConfig(siteToken: string): Promise<WidgetConfig> {
  const res = await widgetFetch<{ data: WidgetConfig }>(`/widget/config`, siteToken);
  return res.data;
}

export async function createVisitor(
  siteToken: string,
  body: { first_name?: string; email?: string } = {}
): Promise<{ id: number }> {
  const res = await widgetFetch<{ data: ApiVisitor }>(`/widget/visitors`, siteToken, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return { id: res.data.id };
}

export async function getOpenConversation(
  siteToken: string,
  visitorId: number
): Promise<{ id: number } | null> {
  const res = await widgetFetch<{ data: ApiConversation | null }>(
    `/widget/conversations?visitor_id=${visitorId}`,
    siteToken
  );
  if (!res.data) return null;
  return { id: res.data.id };
}

export async function createOrGetConversation(
  siteToken: string,
  body: { visitor_id: number; first_name?: string; email?: string }
): Promise<{ id: number }> {
  const res = await widgetFetch<{ data: ApiConversation }>(`/widget/conversations`, siteToken, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return { id: res.data.id };
}

export async function getWidgetMessages(
  siteToken: string,
  conversationId: number
): Promise<
  {
    id: number;
    content: string;
    sender_type: string;
    created_at: string;
    sender?: MessageSender;
    reply_to?: ReplyPreview | null;
    edited_at?: string | null;
    original_content?: string | null;
  }[]
> {
  const res = await widgetFetch<{ data: ApiMessage[] }>(
    `/widget/conversations/${conversationId}/messages`,
    siteToken
  );
  return res.data ?? [];
}

export async function sendWidgetMessage(
  siteToken: string,
  conversationId: number,
  visitorId: number,
  content: string,
  image?: File | null,
  replyToId?: number | null
): Promise<ApiMessage> {
  const hasImage = !!image;
  const hasContent = content != null && content !== '';

  if (hasImage) {
    const formData = new FormData();
    if (hasContent) {
      formData.append('content', content);
    }
    formData.append('image', image as File);
    formData.append('visitor_id', String(visitorId));
    if (replyToId) {
      formData.append('reply_to_id', String(replyToId));
    }

    const res = await widgetFetch<{ data: ApiMessage }>(
      `/widget/conversations/${conversationId}/messages`,
      siteToken,
      {
        method: 'POST',
        body: formData,
      }
    );
    return res.data;
  }

  const res = await widgetFetch<{ data: ApiMessage }>(
    `/widget/conversations/${conversationId}/messages`,
    siteToken,
    {
      method: 'POST',
      body: JSON.stringify({
        content,
        visitor_id: visitorId,
        ...(replyToId ? { reply_to_id: replyToId } : {}),
      }),
    }
  );
  return res.data;
}

/** Modifie le contenu d'un message envoyé par le visiteur (dans les 5 minutes, une seule fois). */
export async function updateWidgetMessage(
  siteToken: string,
  conversationId: number,
  messageId: number,
  visitorId: number,
  content: string
): Promise<ApiMessage> {
  const res = await widgetFetch<{ data: ApiMessage }>(
    `/widget/conversations/${conversationId}/messages/${messageId}`,
    siteToken,
    {
      method: 'PATCH',
      body: JSON.stringify({ content, visitor_id: visitorId }),
    }
  );
  return res.data;
}

/** Statut de présence visiteur : active (widget ouvert) | left (widget fermé). */
export async function updateVisitorPresence(
  siteToken: string,
  visitorId: number,
  status: 'active' | 'left'
): Promise<void> {
  await widgetFetch(`/widget/presence`, siteToken, {
    method: 'PATCH',
    body: JSON.stringify({ visitor_id: visitorId, status }),
  });
}

/** Marque les messages agent d'une conversation comme lus par le visiteur (accusé de lecture). */
export async function markConversationRead(siteToken: string, conversationId: number): Promise<void> {
  await widgetFetch(`/widget/conversations/${conversationId}/mark-read`, siteToken, {
    method: 'POST',
  });
}

/** Indicateur « en train d'écrire » : typing | stopped */
export async function sendTypingStatus(
  siteToken: string,
  conversationId: number,
  visitorId: number,
  status: 'typing' | 'stopped'
): Promise<void> {
  await widgetFetch<{ data: { status: string } }>(
    `/widget/conversations/${conversationId}/typing`,
    siteToken,
    {
      method: 'POST',
      body: JSON.stringify({ visitor_id: visitorId, status }),
    }
  );
}
